import 'vuetify/styles'
//O que mais restaura a aparência do Vuetify 2 é Sass, e vem pelo configFile do
//VuetifyPlugin. Estes são CSS: os três primeiros porque o guia oficial de migração os
//dá assim, e os quatro últimos porque são regra nossa, para o guia não cobrir tudo.
//A ORDEM das camadas não sai daqui: é declarada no public/index.html, que explica por quê.
import '@/styles/vuetify-compat/reset.css'
import '@/styles/vuetify-compat/grid.css'
import '@/styles/vuetify-compat/elevation.css'
import '@/styles/vuetify-compat/icon-button.css'
import '@/styles/vuetify-compat/card.css'
import '@/styles/vuetify-compat/toolbar.css'
import '@/styles/vuetify-compat/data-table-footer.css'
import {createVuetify} from 'vuetify'
import {en, pt, es} from 'vuetify/locale'
import {shortLocale} from '@/i18n'

/**
 * Sem a opção locale, os textos do próprio Vuetify ("Rows per page:", "No data
 * available", "No matching records found") ficam em inglês qualquer que seja o
 * idioma do app. Os locales vêm prontos na biblioteca — aqui é só ligá-los ao
 * idioma que o vue-i18n resolveu.
 *
 * Interlingua não existe no Vuetify e cai no inglês, que é o fallback declarado.
 */
const messages = {en, pt, es}
const locale = shortLocale() in messages ? shortLocale() : 'en'

/**
 * O azul da marca, o MESMO nos dois temas.
 *
 * O valor é o `primary` do tema claro do Vuetify 4, então nada muda para quem já o usa —
 * o que muda é o tema escuro, que trazia um azul mais claro (#2196F3).
 *
 * Isso importa porque a barra do topo é `color="primary"` com `theme="dark"`. O
 * `theme="dark"` é escopado à barra e existe para o conteúdo sair branco sobre o fundo
 * saturado e para os menus que ela abre saírem escuros — é o que o `dark` do v-toolbar
 * fazia no Vuetify 2. Só que lá o `dark` mexia na cor do TEXTO: a classe `.primary` era
 * gerada a partir do tema do app, que é claro, e a barra ficava no #1976D2. No Vuetify 4
 * o `theme` troca a paleta inteira, e a barra passou a puxar o primary do tema escuro —
 * um azul visivelmente mais claro que o de antes.
 *
 * Igualados, `primary` volta a não depender do tema do componente.
 */
const primary = '#1867C0'

export default createVuetify({
    locale: {
        locale,
        fallback: 'en',
        messages
    },
    /**
     * Fixado em 'light'. O padrão do Vuetify 4 passou a ser 'system', que liga o tema
     * escuro conforme a preferência do sistema operacional — o app nunca teve tema
     * escuro, e as cores escritas à mão nas telas não foram pensadas para ele.
     */
    theme: {
        defaultTheme: 'light',
        //Parciais: o Vuetify funde o que vem daqui com os temas padrão
        //(theme.js, parseThemeOptions -> mergeDeep), então só o primary muda.
        themes: {
            light: {colors: {primary}},
            dark: {colors: {primary}}
        }
    },
    /**
     * Caixa de seleção marcada volta a ser 'primary'.
     *
     * No Vuetify 2 isso vinha do mixin `selectable`, que era a base de checkbox, radio e
     * switch: `computedColor` devolvia a cor da prop, e `primary` quando não havia uma.
     * O Vuetify 4 não tem esse padrão — sem cor, o controle marcado fica na cor do texto,
     * ou seja preto.
     *
     * No VSelectionControl, que é o componente que os três desenham por dentro — o mesmo
     * lugar que o mixin ocupava. E o `color` do Vuetify 4 só vale quando o controle está
     * MARCADO (`isActive ? color : baseColor`), que é exatamente a condição que o
     * `computedColor` do 2 fazia à mão.
     *
     * Quem passa `color` na tag continua mandando: o default só entra onde a prop não
     * foi escrita.
     */
    defaults: {
        /**
         * Campos sublinhados, como eram no Vuetify 2.
         *
         * Lá `underlined` era o desenho padrão do v-text-field e companhia, e `outlined`
         * e `filled` eram props que se pediam. O Vuetify 4 inverteu: o padrão virou
         * `filled`, a caixa cinza. São os quatro componentes de campo que o app usa.
         *
         * Quem escreve `variant` na tag continua mandando — os filtros da lista e os
         * menus compactos pedem `outlined` de propósito. E `:variant="x ? 'outlined' :
         * undefined"` também cai aqui: o Vuetify trata `undefined` explícito como prop
         * não passada (defaults.js, propIsDefined).
         */
        VTextField: {variant: 'underlined'},
        VSelect: {variant: 'underlined'},
        VAutocomplete: {variant: 'underlined'},
        VTextarea: {variant: 'underlined'},
        VSelectionControl: {color: 'primary'},
        /**
         * A barra de progresso horizontal volta a ser 'primary'.
         *
         * No Vuetify 2 o `color` do VProgressLinear tinha default 'primary'; no 4 não tem
         * default nenhum, e sem cor a barra sai preta. O VProgressCircular NÃO ganha o
         * mesmo: ele nunca teve default, nas duas versões — os indicadores circulares que
         * aparecem azuis têm o color escrito na tag.
         */
        VProgressLinear: {color: 'primary'},
        /**
         * O rodapé da tabela, de volta ao tamanho do Vuetify 2. Duas escolhas do 4 o
         * engordam, e cada uma se desfaz num lugar diferente:
         *
         * - showFirstLastPage nasce true, e são dois botões redondos a mais na linha; no
         *   Vuetify 2 o padrão era false, e o rodapé tinha só anterior e próxima. Vai em
         *   VDataTable, e NÃO em VDataTableFooter: a tabela declara as props do rodapé
         *   como suas (makeVDataTableFooterProps) e as repassa escritas
         *   (VDataTableFooter.filterProps), e prop escrita pelo pai vence default do
         *   filho — o default do rodapé nunca chegava a ser consultado.
         * - o select de itens por página nasce "outlined", ou seja com caixa desenhada em
         *   volta, onde o 2 tinha só o sublinhado. O default de VSelect lá em cima NÃO o
         *   alcança: o rodapé lê a variante com injectNestedDefaults('VSelect'), que
         *   olha só o que está aninhado no próprio VDataTableFooter, e a repassa ao
         *   componente como prop escrita. Por isso a repetição, que não é redundante.
         *
         * O tamanho da fonte desse select é CSS, e está no vuetify-compat.
         */
        VDataTable: {showFirstLastPage: false},
        VDataTableFooter: {
            VSelect: {variant: 'underlined'}
        }
    },
    /**
     * Os pontos de quebra do Vuetify 2, os mesmos do settings.scss. Sem isto o
     * `$vuetify.display` usaria os do Vuetify 4 (840/1145/1545) enquanto o CSS das
     * colunas usa os do settings.scss, e `smAndDown` discordaria da grade desenhada.
     */
    display: {
        thresholds: {md: 960, lg: 1264, xl: 1904, xxl: 2560}
    }
})
